import { SITE_URL, SITE_NAME, SITE_TAGLINE, SITE_LOGO } from './siteConfig';

type JsonLd = Record<string, unknown>;

function injectJsonLd(id: string, data: JsonLd): void {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export function injectWebSiteSchema(): void {
  injectJsonLd('schema-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    description: SITE_TAGLINE,
  });
}

export function injectOrganizationSchema(): void {
  injectJsonLd('schema-organization', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: SITE_LOGO,
  });
}

export function injectGlobalSchemas(): void {
  injectWebSiteSchema();
  injectOrganizationSchema();
}

export function injectWebPageSchema(path: string, title: string, description: string): void {
  injectJsonLd('schema-webpage', {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    url: `${SITE_URL}${path}`,
    description,
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: `${SITE_URL}/` },
  });
}

export function injectArticleSchema(article: {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified: string;
}): void {
  injectJsonLd('schema-article', {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline,
    description: article.description,
    url: `${SITE_URL}${article.path}`,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/`, logo: SITE_LOGO },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${article.path}` },
  });
}

export function injectFaqSchema(faqs: { q: string; a: string }[]): void {
  injectJsonLd('schema-faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  });
}

export function injectBreadcrumbSchema(items: { name: string; path: string }[]): void {
  injectJsonLd('schema-breadcrumb', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  });
}